import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard, ProductSkeleton } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

export function useProducts(params) {
  const [items, setItems] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    let live = true;
    api.get("/products", { params: JSON.parse(key) }).then((r) => live && setItems(r.data.items)).catch(() => live && setItems([]));
    return () => { live = false; };
  }, [key]);
  return items;
}

export function ProductRail({ id, eyebrow, title, sub, params, to, dark = false }) {
  const { t } = useStore();
  const items = useProducts({ limit: 12, ...params });
  const track = useRef(null);
  const scroll = (d) => track.current?.scrollBy({ left: d * track.current.clientWidth * 0.8, behavior: "smooth" });
  if (items && items.length === 0) return null;
  return (
    <section className={`py-24 sm:py-32 ${dark ? "bg-ink text-sun" : ""}`} data-testid={`rail-${id}`}>
      <div className="container-x">
        <div className="mb-12 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <Reveal>
            {eyebrow && <p className={`eyebrow mb-4 ${dark ? "text-silver" : "text-ink/50"}`}>{eyebrow}</p>}
            <h2 className="font-display text-4xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl">{title}</h2>
            {sub && <p className={`mt-4 max-w-md text-base ${dark ? "text-sun/60" : "text-ink/60"}`}>{sub}</p>}
          </Reveal>
          <div className="flex items-center gap-6">
            <Link to={to} className="link-u font-mono text-xs font-semibold uppercase tracking-[0.2em]" data-testid={`rail-${id}-view-all`}>{t("viewAll")}</Link>
            <div className="hidden gap-2 md:flex">
              <button onClick={() => scroll(-1)} aria-label="Previous" data-testid={`rail-${id}-prev`} className={`grid h-11 w-11 place-items-center border transition-colors ${dark ? "border-sun/20 hover:bg-sun hover:text-ink" : "border-concrete hover:bg-ink hover:text-sun"}`}><ArrowLeft className="h-4 w-4" /></button>
              <button onClick={() => scroll(1)} aria-label="Next" data-testid={`rail-${id}-next`} className={`grid h-11 w-11 place-items-center border transition-colors ${dark ? "border-sun/20 hover:bg-sun hover:text-ink" : "border-concrete hover:bg-ink hover:text-sun"}`}><ArrowRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
        <div ref={track} className={`no-scrollbar -mx-5 flex snap-x snap-mandatory gap-5 overflow-x-auto px-5 sm:-mx-8 sm:px-8 lg:mx-0 lg:px-0 ${dark ? "[&_.card-info]:text-sun [&_.card-info_p]:text-sun/50" : ""}`}>
          {(items || Array.from({ length: 4 })).map((p, i) => (
            <div key={p?.id || i} className="w-[78%] shrink-0 snap-start sm:w-[45%] lg:w-[calc(25%-15px)]">
              {p ? <ProductCard p={p} index={i} /> : <ProductSkeleton />}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
